import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { blogPosts as staticBlogPosts } from '@/data/blogPosts';
import { getTranslatedBlogContent } from '@/data/blogTranslations';
import { localizedRoutes, type RouteKey } from '@/lib/i18n/routes';
import type { Language } from '@/lib/i18n/translations-meta';
import { slugify } from '@/lib/slugify';

function localizeCtaUrl(enUrl: string, lang: string): string {
  if (!enUrl || lang === 'en') return enUrl;
  if (/^(https?:)?\/\//i.test(enUrl)) return enUrl;
  const normalizedUrl = enUrl.startsWith('/') ? enUrl : `/${enUrl}`;
  if (/^\/[a-z]{2}(\/|$)/.test(normalizedUrl)) return normalizedUrl;
  const routeKey = (Object.keys(localizedRoutes.en) as RouteKey[]).find(
    key => localizedRoutes.en[key] === normalizedUrl
  );
  if (routeKey && lang in localizedRoutes) {
    return localizedRoutes[lang as Language][routeKey];
  }
  return `/${lang}${normalizedUrl}`;
}

async function resizeAndOptimizeImage(
  file: File,
  opts: { maxWidth: number; maxHeight: number; quality: number }
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > opts.maxWidth) { height = Math.round(height * (opts.maxWidth / width)); width = opts.maxWidth; }
      if (height > opts.maxHeight) { width = Math.round(width * (opts.maxHeight / height)); height = opts.maxHeight; }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')), 'image/webp', opts.quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

export interface DbBlogPost {
  id: string;
  slug: string;
  category: string;
  author: string;
  date: string;
  read_time: string;
  featured: boolean;
  status: 'draft' | 'published';
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface DbBlogTranslation {
  id: string;
  post_id: string;
  language: string;
  title: string;
  excerpt: string;
  content: string;
  cta_text?: string;
  cta_url?: string;
  pdf_url?: string;
  /** Language-specific URL slug. Falls back to blog_posts.slug when null. */
  slug?: string | null;
}

export interface FullBlogPost extends DbBlogPost {
  translations?: DbBlogTranslation[];
}

export interface NormalizedBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image?: string;
  featured: boolean;
  status: 'draft' | 'published';
  isFromDb: boolean;
  translations?: DbBlogTranslation[];
  ctaText?: string;
  ctaUrl?: string;
  pdfUrl?: string;
  pdfButtonText?: string;
  /** Canonical slug for the requested language (translated if available, else EN). */
  canonicalSlug?: string;
  /** Canonical English slug — used for the `en` and `x-default` hreflang alternates. */
  enSlug?: string;
}

function translatedSlugFor(p: any, language: string): string {
  const t = p.blog_translations?.find((tt: any) => tt.language === language);
  return (t?.slug as string | undefined) || p.slug;
}

function normalizeDbPost(p: any, language: string): NormalizedBlogPost {
  const translation =
    p.blog_translations?.find((t: any) => t.language === language) ||
    p.blog_translations?.find((t: any) => t.language === 'en') ||
    { title: '', excerpt: '', content: '', cta_text: '', cta_url: '', pdf_url: '', pdf_button_text: '' };

  const enTranslation = p.blog_translations?.find((t: any) => t.language === 'en');
  const ctaText = translation.cta_text || enTranslation?.cta_text || '';
  const rawCtaUrl = translation.cta_url || enTranslation?.cta_url || '';
  const ctaUrl = rawCtaUrl ? localizeCtaUrl(rawCtaUrl, language) : '';

  const canonicalSlug = translatedSlugFor(p, language);
  const enSlug = (enTranslation?.slug as string | undefined) || p.slug;

  return {
    id: p.id,
    slug: canonicalSlug,
    enSlug,
    title: translation.title || '',
    excerpt: translation.excerpt || '',
    content: translation.content || '',
    author: p.author,
    date: p.date,
    readTime: p.read_time,
    category: p.category,
    image: p.image_url || undefined,
    featured: p.featured,
    status: p.status as 'draft' | 'published',
    isFromDb: true,
    translations: p.blog_translations,
    ctaText,
    ctaUrl,
    pdfUrl: translation.pdf_url || enTranslation?.pdf_url || '',
    pdfButtonText: translation.pdf_button_text || enTranslation?.pdf_button_text || 'Download PDF',
    canonicalSlug,
  };
}

function normalizeStaticPost(p: (typeof staticBlogPosts)[number], language: string): NormalizedBlogPost {
  return {
    id: p.slug,
    slug: p.slug,
    title: getTranslatedBlogContent(p.slug, language, 'title') || p.title,
    excerpt: getTranslatedBlogContent(p.slug, language, 'excerpt') || p.excerpt,
    content: getTranslatedBlogContent(p.slug, language, 'content') || p.content,
    author: p.author,
    date: p.date,
    readTime: p.readTime,
    category: p.category,
    image: p.image,
    featured: p.featured || false,
    status: 'published',
    isFromDb: false,
    canonicalSlug: p.slug,
  };
}

export function usePublicBlogPosts(language: string, enabled: boolean = true) {
  const [posts, setPosts] = useState<NormalizedBlogPost[]>([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    async function fetchPosts() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select(`*, blog_translations(*)`)
          .eq('status', 'published')
          .order('sort_order', { ascending: true });

        if (!error && data && data.length > 0) {
          setPosts(data.map((p: any) => normalizeDbPost(p, language)));
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('DB fetch failed, falling back to static data', e);
      }

      const normalized: NormalizedBlogPost[] = [...staticBlogPosts]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map(p => normalizeStaticPost(p, language));
      setPosts(normalized);
      setLoading(false);
    }

    fetchPosts();
  }, [language, enabled]);

  return { posts, loading };
}

/**
 * Resolve a blog post by URL slug for a given language.
 * Priority: translated slug → English slug → historical slug (blog_slug_history).
 * When the URL slug does not match the canonical translated slug for the
 * current language, callers should redirect to `post.canonicalSlug`.
 */
async function resolvePostBySlug(slug: string, language: string): Promise<any | null> {
  // 1) Direct: blog_posts.slug (EN canonical)
  {
    const { data } = await supabase
      .from('blog_posts')
      .select(`*, blog_translations(*)`)
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle();
    if (data) return data;
  }

  // 2) Translated slug for the current language. Multiple languages can
  // temporarily share the same slug during backfills, so never query by slug
  // alone: PostgREST returns 406 when `.maybeSingle()` sees multiple rows.
  {
    const candidateLanguages = Array.from(new Set([language, 'en']));
    for (const candidateLanguage of candidateLanguages) {
      const { data: t } = await supabase
        .from('blog_translations')
        .select('post_id')
        .eq('slug', slug)
        .eq('language', candidateLanguage)
        .limit(1)
        .maybeSingle();
      if (t?.post_id) {
        const { data } = await supabase
          .from('blog_posts')
          .select(`*, blog_translations(*)`)
          .eq('id', t.post_id)
          .eq('status', 'published')
          .maybeSingle();
        if (data) return data;
      }
    }
  }

  // 3) Legacy: blog_slug_history via SECURITY DEFINER RPC (public read on
  //    the table itself is restricted; the RPC only returns post_id/language).
  {
    const { data: hist } = await supabase
      .rpc('resolve_blog_slug_history', { _slug: slug, _language: language })
      .limit(1)
      .maybeSingle();
    if (hist?.post_id) {
      const { data } = await supabase
        .from('blog_posts')
        .select(`*, blog_translations(*)`)
        .eq('id', hist.post_id)
        .eq('status', 'published')
        .maybeSingle();
      if (data) return data;
    }
  }


  return null;
}

export function usePublicBlogPost(slug: string | undefined, language: string) {
  const [post, setPost] = useState<NormalizedBlogPost | null>(null);
  const [allPosts, setAllPosts] = useState<NormalizedBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }

    async function fetchPost() {
      setLoading(true);
      // NOTE: never short-circuit to the bundled static copy here. Static posts
      // carry no per-language slugs, so an EN URL matching a static slug would
      // emit hreflang alternates using the EN slug for all 8 languages and break
      // reciprocity with the translated URLs. Always resolve from the DB first;
      // the static data stays as an offline fallback below.
      try {
        const data = await resolvePostBySlug(slug, language);
        if (data) {
          setPost(normalizeDbPost(data, language));
          const { data: allData } = await supabase
            .from('blog_posts')
            .select(`*, blog_translations(*)`)
            .eq('status', 'published')
            .order('sort_order', { ascending: true });
          if (allData) setAllPosts(allData.map((p: any) => normalizeDbPost(p, language)));
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('DB fetch failed, falling back to static data', e);
      }

      const { getBlogPostBySlug, blogPosts: allStaticPosts } = await import('@/data/blogPosts');
      const fallbackStaticPost = getBlogPostBySlug(slug);
      if (fallbackStaticPost) {
        setPost(normalizeStaticPost(fallbackStaticPost, language));
        setAllPosts(allStaticPosts.map(p => normalizeStaticPost(p, language)));
      } else {
        setPost(null);
      }
      setLoading(false);
    }

    fetchPost();
  }, [slug, language]);

  return { post, allPosts, loading };
}

// ============================================================================
// Admin hook — sees all posts including drafts

export type TranslationInput = {
  language: string;
  title: string;
  excerpt: string;
  content: string;
  cta_text?: string;
  cta_url?: string;
  pdf_url?: string;
  /** Optional per-language slug. Auto-derived from `title` when omitted. */
  slug?: string | null;
};

async function ensureUniqueSlug(
  candidate: string,
  language: string,
  excludeTranslationId?: string,
  excludePostSlug?: string
): Promise<string> {
  if (!candidate) return candidate;

  // Collect existing slugs in this language namespace, plus every blog_posts.slug for EN.
  const [{ data: trans }, { data: posts }] = await Promise.all([
    supabase.from('blog_translations').select('id, slug').eq('language', language),
    language === 'en' ? supabase.from('blog_posts').select('slug') : Promise.resolve({ data: [] as any[] }),
  ]);
  const taken = new Set<string>();
  for (const t of (trans ?? []) as any[]) {
    if (t.slug && t.id !== excludeTranslationId) taken.add(t.slug);
  }
  for (const p of (posts ?? []) as any[]) {
    if (p.slug && p.slug !== excludePostSlug) taken.add(p.slug);
  }
  if (!taken.has(candidate)) return candidate;
  let n = 2;
  while (taken.has(`${candidate}-${n}`)) n++;
  return `${candidate}-${n}`;
}

/**
 * Record a legacy slug in blog_slug_history so old URLs keep redirecting.
 * Silent on duplicate — the (language, old_slug) unique constraint handles that.
 */
async function recordSlugHistory(postId: string, language: string, oldSlug: string) {
  if (!oldSlug) return;
  await supabase
    .from('blog_slug_history')
    .upsert({ post_id: postId, language, old_slug: oldSlug }, { onConflict: 'language,old_slug' });
}

export function useAdminBlogPosts() {
  const [posts, setPosts] = useState<FullBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select(`*, blog_translations(*)`)
      .order('sort_order', { ascending: true });

    if (!error && data) {
      setPosts(
        data.map((p: any) => ({
          ...p,
          status: p.status as 'draft' | 'published',
          translations: p.blog_translations,
        }))
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const prepareTranslations = async (
    postId: string,
    postSlug: string,
    translations: TranslationInput[]
  ): Promise<Array<{ post_id: string; language: string; title: string; excerpt: string; content: string; cta_text: string; cta_url: string; pdf_url: string; slug: string }>> => {
    // Preload existing translation slugs for history diff (empty on create).
    const { data: existing } = await supabase
      .from('blog_translations')
      .select('id, language, slug')
      .eq('post_id', postId);
    const existingMap = new Map<string, { id: string; slug: string | null }>();
    for (const e of (existing ?? []) as any[]) existingMap.set(e.language, { id: e.id, slug: e.slug });

    const prepared: Array<{ post_id: string; language: string; title: string; excerpt: string; content: string; cta_text: string; cta_url: string; pdf_url: string; slug: string }> = [];
    for (const t of translations) {
      const prev = existingMap.get(t.language);
      const base = (t.slug && t.slug.trim()) ? slugify(t.slug) : slugify(t.title);
      // EN uses blog_posts.slug as its canonical URL, so translation slug can stay null.
      let finalSlug: string | null = null;
      if (base && t.language !== 'en') {
        finalSlug = await ensureUniqueSlug(base, t.language, prev?.id, postSlug);
      }

      // Record history if slug changed.
      if (t.language !== 'en' && prev?.slug && finalSlug && prev.slug !== finalSlug) {
        await recordSlugHistory(postId, t.language, prev.slug);
      }

      prepared.push({
        post_id: postId,
        language: t.language,
        title: t.title,
        excerpt: t.excerpt,
        content: t.content,
        cta_text: t.cta_text ?? '',
        cta_url: t.cta_url ?? '',
        pdf_url: t.pdf_url ?? '',
        slug: finalSlug ?? '',
      });
    }
    return prepared;
  };

  const createPost = async (
    post: Omit<DbBlogPost, 'id' | 'created_at' | 'updated_at'>,
    translations: TranslationInput[]
  ) => {
    const { data, error } = await supabase
      .from('blog_posts')
      .insert(post)
      .select()
      .single();

    if (error) throw error;

    if (translations.length > 0) {
      const prepared = await prepareTranslations(data.id, data.slug, translations);
      const { error: translError } = await supabase
        .from('blog_translations')
        .insert(prepared);
      if (translError) throw translError;
    }

    await fetchPosts();
    return data;
  };

  const updatePost = async (
    id: string,
    post: Partial<Omit<DbBlogPost, 'id' | 'created_at' | 'updated_at'>>,
    translations?: TranslationInput[]
  ) => {
    // Track EN slug rename so legacy /blog/{old-en-slug} keeps redirecting.
    let previousEnSlug: string | null = null;
    if (post.slug) {
      const { data: prev } = await supabase.from('blog_posts').select('slug').eq('id', id).maybeSingle();
      if (prev?.slug && prev.slug !== post.slug) previousEnSlug = prev.slug;
    }

    const { error } = await supabase
      .from('blog_posts')
      .update(post)
      .eq('id', id);
    if (error) throw error;

    if (previousEnSlug) await recordSlugHistory(id, 'en', previousEnSlug);

    if (translations && translations.length > 0) {
      const prepared = await prepareTranslations(id, post.slug ?? '', translations);
      for (const t of prepared) {
        await supabase
          .from('blog_translations')
          .upsert(t, { onConflict: 'post_id,language' });
      }
    }

    await fetchPosts();
  };

  const deletePost = async (id: string) => {
    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (error) throw error;
    await fetchPosts();
  };

  const uploadImage = async (file: File, onOptimized?: (info: { width: number; height: number; sizeKb: number }) => void): Promise<string> => {
    const optimized = await resizeAndOptimizeImage(file, { maxWidth: 1200, maxHeight: 630, quality: 0.85 });
    if (onOptimized) {
      const bmp = await createImageBitmap(optimized);
      onOptimized({ width: bmp.width, height: bmp.height, sizeKb: Math.round(optimized.size / 1024) });
      bmp.close();
    }
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.webp`;
    const { data, error } = await supabase.storage
      .from('blog-images')
      .upload(fileName, optimized, { contentType: 'image/webp' });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from('blog-images').getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const reorderPosts = async (orderedIds: string[]) => {
    for (let i = 0; i < orderedIds.length; i++) {
      await supabase.from('blog_posts').update({ sort_order: i + 1 }).eq('id', orderedIds[i]);
    }
    await fetchPosts();
  };

  /**
   * Backfill translated slugs for every post × language that lacks one.
   * Delegates to the translate-blog edge function (mode: 'slugs-only') so the
   * generation logic stays server-side (uses SERVICE_ROLE to bypass RLS for
   * history writes across all posts).
   */
  const backfillTranslatedSlugs = async () => {
    const { data, error } = await supabase.functions.invoke('translate-blog', {
      body: { mode: 'slugs-only' },
    });
    if (error) throw error;
    await fetchPosts();
    return data as { updated: number; results: Array<{ post_id: string; language: string; slug: string; previous: string | null }> };
  };

  return { posts, loading, createPost, updatePost, deletePost, uploadImage, reorderPosts, backfillTranslatedSlugs, refetch: fetchPosts };
}

export interface BlogImage {
  name: string;
  url: string;
  createdAt: string;
}

export function useBlogImages() {
  const [images, setImages] = useState<BlogImage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.storage
      .from('blog-images')
      .list('', { sortBy: { column: 'created_at', order: 'desc' }, limit: 100 });

    if (!error && data) {
      const mapped: BlogImage[] = data
        .filter(f => f.name !== '.emptyFolderPlaceholder')
        .map(f => ({
          name: f.name,
          url: supabase.storage.from('blog-images').getPublicUrl(f.name).data.publicUrl,
          createdAt: f.created_at ?? '',
        }));
      setImages(mapped);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  return { images, loading, refetch: fetchImages };
}
